<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-truck me-2"></i>Supplier Details
      </h4>
      <div>
        <button class="btn btn-outline-secondary" @click="goToSuppliers">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <!-- Loading Spinner -->
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading supplier details...</p>
      </div>

      <!-- Supplier Content -->
      <div v-if="!loading" class="supplier-details-content fade-in">
        <!-- Supplier Header -->
        <div class="text-center mb-4 supplier-header">
          <h1 class="supplier-title">{{ supplier.name }}</h1>
          <div class="d-flex justify-content-center align-items-center mt-2">
            <Badge pill :type="supplier.status === 'active' ? 'success' : 'danger'" class="me-3">
              {{ supplier.status }}
            </Badge>
            <Badge pill type="info">
              {{ supplier.type }}
            </Badge>
          </div>
        </div>

        <!-- Tabs Navigation -->
        <ul class="nav nav-tabs nav-fill mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{ active: activeTab === 'details' }" @click="activeTab = 'details'" href="#">Details</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{ active: activeTab === 'direct_purchases' }" @click="activeTab = 'direct_purchases'" href="#">Direct Purchases</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{ active: activeTab === 'orders' }" @click="activeTab = 'orders'" href="#">Orders</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{ active: activeTab === 'purchases' }" @click="activeTab = 'purchases'" href="#">Purchases</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{ active: activeTab === 'payments' }" @click="activeTab = 'payments'" href="#">Payments</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{ active: activeTab === 'files' }" @click="activeTab = 'files'" href="#">Files</a>
          </li>
        </ul>

        <!-- Tab Content -->
        <div class="tab-content">
          <!-- Details Tab -->
          <div v-if="activeTab === 'details'">
            <!-- Basic Information Section -->
            <div class="card border-0 shadow-sm mb-4">
              <div class="card-header bg-light py-3">
                <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Basic Information</h5>
              </div>
              <div class="card-body">
                <div class="row g-3">
                  <div class="col-md-6 col-lg-3">
                    <div class="info-group">
                      <h6 class="info-label">Code</h6>
                      <p class="info-value">{{ supplier.code }}</p>
                    </div>
                  </div>
                  <div class="col-md-6 col-lg-3">
                    <div class="info-group">
                      <h6 class="info-label">Email</h6>
                      <p class="info-value">{{ supplier.email || 'Not provided' }}</p>
                    </div>
                  </div>
                  <div class="col-md-6 col-lg-3">
                    <div class="info-group">
                      <h6 class="info-label">Phone</h6>
                      <p class="info-value">{{ supplier.phone || 'Not provided' }}</p>
                    </div>
                  </div>
                  <div class="col-md-6 col-lg-3">
                    <div class="info-group">
                      <h6 class="info-label">Address</h6>
                      <p class="info-value">{{ supplier.address || 'Not provided' }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Banking Information Section -->
            <div class="card border-0 shadow-sm mb-4">
              <div class="card-header bg-light py-3">
                <h5 class="mb-0"><i class="bi bi-bank me-2"></i>Banking Information</h5>
              </div>
              <div class="card-body">
                <div class="row g-3">
                  <div class="col-md-6 col-lg-3">
                    <div class="info-group">
                      <h6 class="info-label">Account Name</h6>
                      <p class="info-value">{{ supplier.account_name || 'Not provided' }}</p>
                    </div>
                  </div>
                  <div class="col-md-6 col-lg-3">
                    <div class="info-group">
                      <h6 class="info-label">Account Number</h6>
                      <p class="info-value">{{ supplier.account_no || 'Not provided' }}</p>
                    </div>
                  </div>
                  <div class="col-md-6 col-lg-3">
                    <div class="info-group">
                      <h6 class="info-label">Bank Name</h6>
                      <p class="info-value">{{ supplier.bank_name || 'Not provided' }}</p>
                    </div>
                  </div>
                  <div class="col-md-6 col-lg-3">
                    <div class="info-group">
                      <h6 class="info-label">Branch Name</h6>
                      <p class="info-value">{{ supplier.branch_name || 'Not provided' }}</p>
                    </div>
                  </div>
                  <div class="col-md-6 col-lg-3">
                    <div class="info-group">
                      <h6 class="info-label">Bank Code</h6>
                      <p class="info-value">{{ supplier.bank_code || 'Not provided' }}</p>
                    </div>
                  </div>
                  <div class="col-md-6 col-lg-3">
                    <div class="info-group">
                      <h6 class="info-label">Branch Code</h6>
                      <p class="info-value">{{ supplier.branch_code || 'Not provided' }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Direct Purchases Tab -->
          <div v-if="activeTab === 'direct_purchases'">
            <div v-if="relatedDataLoading" class="text-center py-5">
              <Spinner spinner-class="mb-1" />
              <p class="text-muted mt-2">Loading direct purchases...</p>
            </div>
            <div v-else>
              <div v-if="directPurchases.length > 0">
                <table class="table table-striped">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Created By</th>
                      <th>Date</th>
                      <th>Invoice/Receipt</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="dp in directPurchases" :key="dp.id">
                      <td>{{ dp.code }}</td>
                      <td>{{ dp.user.first_name }} {{ dp.user.last_name }}</td>
                      <td>{{ formatDate(dp.created_at) }}</td>
                      <td>{{ dp.invoice }}</td>
                      <td><Badge :type="dp.status === 'complete' ? 'success' : 'warning'">{{ dp.status }}</Badge></td>
                      <td>
                        <button class="btn btn-sm btn-outline-primary" @click="directPurchaseDetails(dp.id)">
                          <i class="bi bi-eye"></i> Details
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-center py-5 bg-light rounded-3">
                <vue-feather type="shopping-cart" size="48" class="text-muted mb-3"></vue-feather>
                <h5 class="text-muted">No direct purchases found</h5>
                <p class="text-muted small mb-0">Direct purchases from this supplier will appear here.</p>
                <button class="btn btn-primary mt-3" @click="goToDirectPurchases">
                  <i class="bi bi-plus-circle me-1"></i> Go to Direct Purchases
                </button>
              </div>
            </div>
          </div>

          <!-- Orders Tab -->
          <div v-if="activeTab === 'orders'">
            <div v-if="relatedDataLoading" class="text-center py-5">
              <Spinner spinner-class="mb-1" />
              <p class="text-muted mt-2">Loading orders...</p>
            </div>
            <div v-else>
              <div v-if="orders.length > 0">
                <table class="table table-striped">
                  <thead>
                    <tr>
                      <th>Order No</th>
                      <th>Created By</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="order in orders" :key="order.id">
                      <td>{{ order.code }}</td>
                      <td>{{ order.user.first_name }} {{ order.user.last_name }}</td>
                      <td>{{ formatDate(order.created_at) }}</td>
                      <td><Badge :type="order.status === 'complete' ? 'success' : 'warning'">{{ order.status }}</Badge></td>
                      <td>
                        <button class="btn btn-sm btn-outline-primary" @click="orderDetails(order.id)">
                          <i class="bi bi-eye"></i> Details
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-center py-5 bg-light rounded-3">
                <vue-feather type="package" size="48" class="text-muted mb-3"></vue-feather>
                <h5 class="text-muted">No orders found</h5>
                <p class="text-muted small mb-0">Orders from this supplier will appear here.</p>
                <button class="btn btn-primary mt-3" @click="goToOrders">
                  <i class="bi bi-plus-circle me-1"></i> Go to Orders
                </button>
              </div>
            </div>
          </div>

          <!-- Purchases Tab -->
          <div v-if="activeTab === 'purchases'">
            <div v-if="relatedDataLoading" class="text-center py-5">
              <Spinner spinner-class="mb-1" />
              <p class="text-muted mt-2">Loading purchases...</p>
            </div>
            <div v-else>
              <div v-if="purchases.length > 0">
                <table class="table table-striped">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Created By</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="purchase in purchases" :key="purchase.id">
                      <td>{{ purchase.code }}</td>
                      <td>{{ purchase.user.first_name }} {{ purchase.user.last_name }}</td>
                      <td>{{ formatDate(purchase.created_at) }}</td>
                      <td><Badge :type="purchase.status === 'complete' ? 'success' : 'warning'">{{ purchase.status }}</Badge></td>
                      <td>
                        <button class="btn btn-sm btn-outline-primary" @click="purchaseDetails(purchase.id)">
                          <i class="bi bi-eye"></i> Details
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-center py-5 bg-light rounded-3">
                <vue-feather type="shopping-bag" size="48" class="text-muted mb-3"></vue-feather>
                <h5 class="text-muted">No purchases found</h5>
                <p class="text-muted small mb-0">Purchases from this supplier will appear here.</p>
                <button class="btn btn-primary mt-3" @click="goToPurchases">
                  <i class="bi bi-plus-circle me-1"></i> Go to Purchases
                </button>
              </div>
            </div>
          </div>

          <!-- Payments Tab -->
          <div v-if="activeTab === 'payments'">
            <div v-if="relatedDataLoading" class="text-center py-5">
              <Spinner spinner-class="mb-1" />
              <p class="text-muted mt-2">Loading payments...</p>
            </div>
            <div v-else>
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <span class="badge bg-primary rounded-pill me-2">{{ payments.length }}</span>
                  <span class="text-muted">Total Payments</span>
                </div>
                <button v-if="profile.role !== 'user'" class="btn btn-primary btn-sm" @click="paymentModal = true; paymentMode = 'add'">
                  <i class="bi bi-plus-circle me-1"></i> Add Payment
                </button>
              </div>

              <div v-if="payments.length > 0">
                <table class="table table-hover">
                  <thead class="table-light">
                    <tr>
                      <th>CODE</th>
                      <th>PURCHASE</th>
                      <th>AMOUNT</th>
                      <th>METHOD</th>
                      <th>DUE DATE</th>
                      <th>PAYMENT DATE</th>
                      <th>STATUS</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="payment in payments" :key="payment.id">
                      <td>{{ payment.code }}</td>
                      <td>
                        <a v-if="payment.purchase" :href="`/purchase/${payment.purchase.id}`" class="text-decoration-none">{{ payment.purchase.code }}</a>
                        <span v-else>-</span>
                      </td>
                      <td>{{ formatNumber(payment.amount) }}</td>
                      <td>
                        <span class="badge" :class="getPaymentMethodBadge(payment.payment_method)">{{ formatPaymentMethod(payment.payment_method) }}</span>
                      </td>
                      <td>{{ formatDate(payment.due_date) }}</td>
                      <td>{{ payment.payment_date ? formatDate(payment.payment_date) : '-' }}</td>
                      <td>
                        <Badge :type="getPaymentStatusBadge(payment.status)">{{ payment.status }}</Badge>
                      </td>
                      <td>
                        <button v-if="payment.status === 'pending'" class="btn btn-sm btn-outline-success me-1" @click="handleApprovePayment(payment)">
                          <i class="bi bi-check-circle"></i> Approve
                        </button>
                        <button v-if="profile.role !== 'user' && payment.status === 'pending'" class="btn btn-sm btn-outline-primary me-1" @click="editPayment(payment)">
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button v-if="profile.role !== 'user' && payment.status === 'pending'" class="btn btn-sm btn-outline-danger" @click="deletePayment(payment.id)">
                          <i class="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-center py-5 bg-light rounded-3">
                <vue-feather type="credit-card" size="48" class="text-muted mb-3"></vue-feather>
                <h5 class="text-muted">No payments found</h5>
                <p class="text-muted small mb-0">Payments to this supplier will appear here.</p>
                <button class="btn btn-primary mt-3" @click="paymentModal = true; paymentMode = 'add'">
                  <i class="bi bi-plus-circle me-1"></i> Add Payment
                </button>
              </div>
            </div>
          </div>

          <!-- Files Tab -->
          <div v-if="activeTab === 'files'">
            <div class="pt-3">
              <div class="d-flex justify-content-end align-items-center mb-3">
                <button v-if="profile.role !== 'user'" class="btn btn-primary btn-sm" @click="fileModal = true">
                  <vue-feather type="upload" size="16" class="me-1"></vue-feather>
                  Add File
                </button>
              </div>

              <div v-if="supplier.files.length === 0" class="text-center py-5 bg-light rounded-3">
                <vue-feather type="file" size="48" class="text-muted mb-3"></vue-feather>
                <h5 class="text-muted">No files found</h5>
                <p class="text-muted small mb-0">Upload files to see them here</p>
                <button class="btn btn-primary mt-3" @click="fileModal = true">
                  <i class="bi bi-plus-circle me-1"></i> Add File
                </button>
              </div>

              <div v-else class="table-responsive">
                <table class="table table-hover border-0">
                  <thead class="table-light">
                  <tr>
                    <th scope="col" class="border-0">Name</th>
                    <th scope="col" class="border-0">Description</th>
                    <th scope="col" class="border-0">Created</th>
                    <th scope="col" class="border-0 text-end">Actions</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="file in supplier.files" :key="file.id">
                    <td class="align-middle">
                      <div class="d-flex align-items-center">
                        <vue-feather
                            :type="getFileIcon(file.file)"
                            size="16"
                            class="text-primary me-2"
                        ></vue-feather>
                        {{ file.name }}
                      </div>
                    </td>
                    <td class="align-middle">
                        <span v-if="file.description" class="text-truncate d-inline-block" style="max-width: 250px;" :title="file.description">
                          {{ file.description }}
                        </span>
                      <span v-else class="text-muted fst-italic">—</span>
                    </td>
                    <td class="align-middle">{{ formatDate(file.created_at) }}</td>
                    <td class="align-middle text-end">
                      <button
                          class="btn btn-outline-primary btn-sm"
                          @click="viewFile(file.url)"
                          title="View File"
                      >
                        <vue-feather type="eye" size="14"></vue-feather>
                      </button>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-center gap-3 mt-4 mb-3">
          <button class="btn btn-outline-secondary px-4" @click="goToSuppliers">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
          <button
            v-if="profile.role !== 'user' && profile.role !== 'admin'"
            class="btn btn-primary px-4"
            @click="$router.push({path: '/edit-supplier/' + $route.params.id})">
            <i class="bi bi-pencil me-1"></i> Edit Supplier
          </button>
          <button
            v-if="profile.role !== 'user' && profile.role !== 'admin'"
            class="btn btn-success px-4"
            @click="$router.push({path: '/supplier/' + $route.params.id + '/products'})">
            <i class="bi bi-box-seam me-1"></i> View Supplier Products
          </button>
        </div>
      </div>
    </div>
  </Card>

  <!-- Payment Modal -->
  <AddPaymentModal
    :open="paymentModal"
    :mode="paymentMode"
    :payment="payment"
    :purchases="purchases"
    :preload-supplier-id="$route.params.id"
    :hide-supplier-id="true"
    @closed="closePaymentModal"
    @submit="handlePaymentSubmit"
  />
  <!-- Payment Modal End -->

  <!-- Add File Modal -->
  <Modal :open="fileModal" @closed="fileModal = false" dialog-size="modal-md">
    <div class="modal-header border-0">
      <h5 class="modal-title m-0 d-flex align-items-center">
        <vue-feather type="upload" size="18" class="text-primary me-2"></vue-feather>
        Upload File
      </h5>
      <button @click="fileModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
      </button>
    </div>
    <div class="modal-body">
      <form @submit.prevent="">
        <div class="row g-3">
          <div class="col-12">
            <label for="uploadName" class="form-label">File Name</label>
            <div class="input-group">
                  <span class="input-group-text bg-light">
                    <vue-feather type="file" size="16"></vue-feather>
                  </span>
              <input
                  type="text"
                  class="form-control"
                  list="uploadOptions"
                  id="uploadName"
                  v-model="file.name"
                  placeholder="Enter file name or select from suggestions"
                  required
              >
            </div>
            <datalist id="uploadOptions">
              <option v-for="upload in suggestions.files" :key="upload" :value="upload"></option>
            </datalist>
          </div>

          <div class="col-12">
            <label class="form-label">File Upload</label>
            <div class="input-group">
              <input
                  class="form-control"
                  type="file"
                  @input="handleFileUpload"
                  name="docs"
                  accept=".jpeg,.png,.jpg,.pdf"
                  required
              >
            </div>
            <small class="text-muted">Supported formats: JPEG, PNG, JPG, PDF</small>
          </div>

          <div class="col-12">
            <label for="fileDescription" class="form-label">Description</label>
            <textarea
                id="fileDescription"
                class="form-control"
                v-model="file.description"
                rows="3"
                placeholder="Enter a description for this file (optional)"
            ></textarea>
          </div>
        </div>
        <div class="row modal-footer border-0">
          <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="fileModal = false">
            <vue-feather type="x" size="16" class="me-1"></vue-feather>
            Cancel
          </button>
          <button type="submit" class="btn btn-primary" @click="addFile">
            <vue-feather type="upload" size="16" class="me-1"></vue-feather>
            Upload
          </button>
        </div>
      </form>
    </div>
  </Modal>
  <!-- Add File Modal End -->

</template>

<script>
import supplierService from "@/api/supplierService";
import supplierPaymentService from "@/api/supplierPaymentService";
import formatDate from "../../utils/formatDate";
import {decrypt} from "@/utils/crypto";
import formatNumber from "../../utils/formatNumber";
import confirmDialog from "@/utils/confirmDialog";
import Badge from "@/components/badge/Badge";
import AddPaymentModal from "@/components/modal/AddPaymentModal.vue";
import loadingButton from "@/utils/loadingButton";
import notify from "@/utils/notify";

export default {
  name: "SupplierDetails",
  components: {
    Badge,
    AddPaymentModal
  },

  data() {
    return {
      activeTab: 'details',
      profile: {},
      loading: true,
      relatedDataLoading: true,
      showModal: false,
      supplier: {},
      directPurchases: [],
      orders: [],
      purchases: [],
      payments: [],
      paymentModal: false,
      paymentMode: 'add',
      payment: {
        organization_id: '',
        purchase_id: '',
        supplier_id: '',
        user_id: '',
        code: '',
        amount: '',
        payment_method: '',
        reference: '',
        status: 'pending',
        due_date: '',
        payment_date: null,
        notes: ''
      },
      imagesUrl: process.env.VUE_APP_API_IMAGE_URL + 'supplier/',
      fileModal: false,
      suggestions: {
        files: []
      },
      file: {
        name: '',
        file: '',
        description: ''
      },
      selectedFile: null
    }
  },

  methods: {
    formatNumber,
    formatDate,
    confirmDialog,
    async getSupplier() {
      try {
        this.supplier = await supplierService.getSupplier(this.$route.params.id);
      } catch (error) {
        console.error("Error fetching supplier details:", error);
      } finally {
        this.loading = false;
      }
    },
    async getRelatedData() {
      try {
        const relatedData = await supplierService.getSupplierRelatedData(this.$route.params.id);
        this.directPurchases = relatedData.direct_purchases;
        this.orders = relatedData.orders;
        this.purchases = relatedData.purchases;
        await this.getPayments();
      } catch (error) {
        console.error("Error fetching supplier related data:", error);
      } finally {
        this.relatedDataLoading = false;
      }
    },
    async getPayments() {
      try {
        const paymentsData = await supplierPaymentService.getSupplierPaymentsBySupplier(this.$route.params.id);
        this.payments = paymentsData?.data || [];
      } catch (error) {
        console.error("Error fetching payments:", error);
        this.payments = [];
      }
    },
    formatPaymentMethod(method) {
      return method ? method.replace('_', ' ').toUpperCase() : '-';
    },
    getPaymentMethodBadge(method) {
      switch (method) {
        case 'Bank Transfer':
          return 'bg-primary';
        case 'Mpesa':
          return 'bg-success';
        case 'Cheque':
          return 'bg-warning';
        case 'Credit':
          return 'bg-info';
        case 'Cash':
          return 'bg-secondary';
        default:
          return 'bg-secondary';
      }
    },
    getPaymentStatusBadge(status) {
      switch (status) {
        case 'completed':
          return 'success';
        case 'pending':
          return 'warning';
        case 'cancelled':
          return 'danger';
        default:
          return 'secondary';
      }
    },
    closePaymentModal() {
      this.paymentModal = false;
      this.paymentMode = 'add';
      this.payment = {
        organization_id: '',
        purchase_id: '',
        supplier_id: '',
        user_id: '',
        code: '',
        amount: '',
        payment_method: '',
        reference: '',
        status: 'pending',
        due_date: '',
        payment_date: null,
        notes: ''
      };
    },
    editPayment(payment) {
      this.payment = {...payment};
      this.paymentMode = 'edit';
      this.paymentModal = true;
    },
    async handlePaymentSubmit(paymentData) {
      if (this.paymentMode === 'add') {
        await this.createPayment(paymentData);
      } else {
        await this.updatePayment(paymentData);
      }
    },
    async createPayment(paymentData) {
      const response = await supplierPaymentService.createSupplierPayment(paymentData);
      if (response) {
        this.closePaymentModal();
        await this.getPayments();
      }
    },
    async updatePayment(paymentData) {
      const response = await supplierPaymentService.updateSupplierPayment(paymentData, paymentData.id);
      if (response) {
        this.closePaymentModal();
        await this.getPayments();
      }
    },
    handleApprovePayment(payment) {
      if (!payment.reference || payment.reference.trim() === '') {
        notify.alert.info('Add the payment reference');
        return;
      }
      confirmDialog('Approve Payment', () => this.approvePayment(payment.id), 'Are you sure you want to approve this payment?');
    },
    async approvePayment(id) {
      const response = await supplierPaymentService.approveSupplierPayment(id);
      if (response) {
        await this.getPayments();
      }
    },
    async deletePayment(id) {
      if (confirm('Are you sure you want to delete this payment?')) {
        const response = await supplierPaymentService.deleteSupplierPayment(id);
        if (response) {
          await this.getPayments();
        }
      }
    },
    getFileIcon(fileName) {
      const extension = fileName.split('.').pop().toLowerCase();

      switch (extension) {
        case 'pdf':
          return 'file-text';
        case 'doc':
        case 'docx':
          return 'file-text';
        case 'xls':
        case 'xlsx':
          return 'file';
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
          return 'image';
        default:
          return 'file';
      }
    },
    viewFile(url) {
      window.open(url, '_blank');
    },
    handleFileUpload(file) {
      this.selectedFile = file
    },
    addFile : async function(e) {
      if (this.selectedFile === null) {
        notify.alert.error('Please select a file')
      } else {
        loadingButton(e)
        let fileReader = this.processFile(this.selectedFile)
        fileReader.onload = async () => {
          if (typeof fileReader.result === 'string')
            this.file.file = fileReader.result
          let response = await supplierService.uploadSupplierFile(this.file, this.$route.params.id)
          if (response)
            this.fileModal = false
            await this.getSupplier()
        }
        loadingButton(e, false)
      }
    },
    processFile : function(file) {
      const fileReader = new FileReader()
      const { files } = file.target
      if (files && files.length) {
        fileReader.readAsDataURL(files[0])
        return fileReader
      }
    },
    goToSuppliers() {
      this.$router.push('/suppliers');
    },
    orderDetails(id) {
      this.$router.push('/order/' + id);
    },
    purchaseDetails(id) {
      this.$router.push('/purchase/' + id);
    },
    directPurchaseDetails(id) {
      this.$router.push('/direct-purchase/' + id);
    },
    goToDirectPurchases() {
      this.$router.push('/direct-purchases');
    },
    goToOrders() {
      this.$router.push('/orders');
    },
    goToPurchases() {
      this.$router.push('/purchases');
    },
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    await this.getSupplier()
    await this.getRelatedData()
  },

  watch: {}
}
</script>

<style scoped>
/* Custom tab styles */
.nav-tabs {
  border-bottom: 2px solid #dee2e6;
}

.nav-tabs .nav-item {
  margin-bottom: -2px;
}

.nav-tabs .nav-link {
  border: 2px solid transparent;
  border-top-left-radius: .35rem;
  border-top-right-radius: .35rem;
  padding: .75rem 1rem;
  font-size: 1.05rem;
  color: #6c757d;
  transition: all 0.2s ease-in-out;
}

.nav-tabs .nav-link:hover {
  border-color: #e9ecef #e9ecef #dee2e6;
  background-color: #f8f9fa;
  color: #495057;
}

.nav-tabs .nav-link.active {
  color: #0d6efd;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
  border-bottom: 2px solid #fff;
  font-weight: 600;
}

/* Supplier details styles */
.supplier-details-content {
  padding: 0.5rem;
}

.supplier-header {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0.375rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
}

.supplier-title {
  font-weight: 100;
  color: #333;
  margin-bottom: 0.5rem;
}

.info-group {
  margin-bottom: 0.5rem;
}

.info-label {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.3rem;
  color: #555;
}

.info-value {
  font-size: 1rem;
  color: #333;
}

.card-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.card {
  transition: all 0.3s ease;
  overflow: hidden;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.05) !important;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .info-label {
    font-size: 0.85rem;
  }

  .info-value {
    font-size: 0.95rem;
  }
}
</style>
